"use client"
import { useEffect, useRef, useState } from "react"
import { resolveJsEasing } from "@/app/playbook/animation/animationTuningDefaults"
import {
  setLayoutAllowTiny,
  type ComputeFragment,
  type ComputeFragmentRect,
} from "./powerTreemapLayout"

// ─────────────────────────────────────────────────────────────────────────────
// Continuous treemap transition engine (value-morph, rebuilt from first
// principles).
//
// Earlier versions interpolated each cell's RECTANGLE between the old and new
// layouts. That is only overlap-free when both layouts tile the SAME tree — true
// for the tidy branch plans, but NOT for Plan A, whose cell set churns every
// step (labs cross the frontier in/out of the "Rest of X" aggregate, tail
// companies get flagship ids, …). Different trees → interpolated rectangles
// cross → overlap.
//
// So this engine never interpolates rectangles. It interpolates the underlying
// COMPUTE VALUES and re-runs the real treemap layout every frame. A treemap
// layout always tiles its region with no overlaps, so EVERY frame is overlap-
// free by construction, no matter how the cell set changes. A cell that is new
// this step grows from ~0; a cell that's leaving shrinks to ~0 (the layout runs
// in allow-tiny mode during the morph so cells can pass through ~0 smoothly).
// That is exactly "splitting and shrinking of entries" — a "Rest of X" box
// shrinks while the companies that split out of it grow in the same slot, one
// continuous motion, no element ever swapped for another. Labels only fade
// (in when a cell arrives, out when it leaves); they never fly across the map.
// ─────────────────────────────────────────────────────────────────────────────

export type TransitionCell = ComputeFragmentRect & { labelOpacity: number }
export type TransitionFrame = { cells: TransitionCell[] }
export type TransitionOptions = { durationMs: number; easing: string; labelFadeMs: number }

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

// Region paint order — matches getComputeFragments (US → CH → RoW) so the union
// stays region-contiguous, which the region-stable layout relies on.
const REGION_ORDER: Record<string, number> = { america: 0, china: 1, world: 2 }

const compareUnion = (a: ComputeFragment, b: ComputeFragment): number => {
  const ra = REGION_ORDER[a.lab.regionId] ?? 99
  const rb = REGION_ORDER[b.lab.regionId] ?? 99
  if (ra !== rb) return ra - rb
  const na = a.id.match(/-(?:slot|tail)-(\d+)$/)
  const nb = b.id.match(/-(?:slot|tail)-(\d+)$/)
  if (na && nb) return Number(na[1]) - Number(nb[1])
  if (na) return 1
  if (nb) return -1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

const signatureOf = (fragments: ComputeFragment[]): string =>
  fragments.map(f => `${f.id}:${Math.round(f.value)}`).join("|")

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true

// One entry in the morph: a stable fragment identity with a from/to value and a
// from/to label opacity.
type MorphEntry = {
  fragment: ComputeFragment
  fromValue: number
  toValue: number
  fromLabel: number
  toLabel: number
}

const layoutWithTiny = (
  layout: (fragments: ComputeFragment[]) => ComputeFragmentRect[],
  fragments: ComputeFragment[],
): ComputeFragmentRect[] => {
  setLayoutAllowTiny(true)
  try {
    return layout(fragments)
  } finally {
    setLayoutAllowTiny(false)
  }
}

export const useTreemapTransition = (
  targetFragments: ComputeFragment[],
  layout: (fragments: ComputeFragment[]) => ComputeFragmentRect[],
  options: TransitionOptions,
): TransitionFrame => {
  // Idle frame: the plain (folded, min-clamped) production layout of the current
  // fragments, recomputed each render so resizes/hover just work.
  const idleFrame = (): TransitionFrame => ({
    cells: layout(targetFragments).map(r => ({ ...r, labelOpacity: 1 })),
  })

  const [frame, setFrame] = useState<TransitionFrame>(idleFrame)
  const animatingRef = useRef(false)
  const signatureRef = useRef<string | null>(null)
  const previousRef = useRef<ComputeFragment[]>(targetFragments)
  // Value actually displayed per id right now (so an interrupted morph continues
  // from where it is rather than snapping).
  const displayedValueRef = useRef<Map<string, number>>(
    new Map(targetFragments.map(f => [f.id, f.value])),
  )
  const rafRef = useRef<number | null>(null)
  const layoutRef = useRef(layout)
  layoutRef.current = layout
  const optionsRef = useRef(options)
  optionsRef.current = options

  const stop = () => { if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null } }
  useEffect(() => () => stop(), [])

  useEffect(() => {
    const signature = signatureOf(targetFragments)
    if (signature === signatureRef.current) return
    signatureRef.current = signature

    const previous = previousRef.current
    previousRef.current = targetFragments
    const { durationMs, easing, labelFadeMs } = optionsRef.current

    const settle = () => {
      stop()
      animatingRef.current = false
      displayedValueRef.current = new Map(targetFragments.map(f => [f.id, f.value]))
      setFrame({ cells: layoutRef.current(targetFragments).map(r => ({ ...r, labelOpacity: 1 })) })
    }
    if (durationMs <= 0 || prefersReducedMotion() || previous.length === 0) { settle(); return }

    // Build the union of previous and target cells, each with from/to values.
    const displayed = displayedValueRef.current
    const targetById = new Map(targetFragments.map(f => [f.id, f]))
    const byId = new Map<string, MorphEntry>()
    for (const f of targetFragments) {
      const fromValue = displayed.get(f.id) ?? 0            // new cell → grow from 0
      byId.set(f.id, {
        fragment: f,
        fromValue,
        toValue: f.value,
        fromLabel: displayed.has(f.id) ? 1 : 0,            // new label fades in
        toLabel: 1,
      })
    }
    for (const f of previous) {
      if (targetById.has(f.id)) continue                   // handled above
      const fromValue = displayed.get(f.id) ?? f.value     // leaving cell → shrink to 0
      byId.set(f.id, { fragment: f, fromValue, toValue: 0, fromLabel: 1, toLabel: 0 })
    }
    const entries = [...byId.values()].sort((a, b) => compareUnion(a.fragment, b.fragment))
    if (entries.length === 0) { settle(); return }

    // Seed the layout's row grouping from the TARGET values (each cell at where
    // it's headed), so the whole morph is squarified for the final state and the
    // first frame — cells at their OLD sizes in that same grouping — matches what
    // was on screen. (Computing the grouping from the first, old-valued frame
    // instead makes the layout snap once as the morph starts.)
    layoutWithTiny(layoutRef.current, entries.map(e => ({
      id: e.fragment.id,
      lab: e.fragment.lab,
      value: Math.max(0, e.toValue),
    })))

    stop()
    animatingRef.current = true
    const ease = resolveJsEasing(easing)
    const start = performance.now()
    const labelFrac = durationMs > 0 ? Math.min(1, Math.max(0, labelFadeMs / durationMs)) : 1

    const tick = () => {
      const raw = Math.min(1, (performance.now() - start) / durationMs)
      if (raw >= 1) { settle(); return }
      const t = ease(raw)
      const labelT = labelFrac > 0 ? Math.min(1, raw / labelFrac) : 1
      // Interpolated fragments (a fresh object per frame so we never mutate the
      // caller's data), then the REAL layout — one valid tiling, zero overlap.
      const morphFragments = entries.map(e => ({
        id: e.fragment.id,
        lab: e.fragment.lab,
        value: Math.max(0, lerp(e.fromValue, e.toValue, t)),
      }))
      const rects = layoutWithTiny(layoutRef.current, morphFragments)
      const labelById = new Map(entries.map(e => [e.fragment.id, lerp(e.fromLabel, e.toLabel, labelT)]))
      const nextValue = new Map(morphFragments.map(f => [f.id, f.value]))
      displayedValueRef.current = nextValue
      setFrame({ cells: rects.map(r => ({ ...r, labelOpacity: labelById.get(r.fragment.id) ?? 1 })) })
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [targetFragments])

  // While animating, the rAF owns the frame. When a NEW target has just arrived
  // but the morph effect hasn't run yet (this render is before the effect),
  // keep showing the last committed frame — otherwise we'd paint the new layout
  // for a frame or two and then snap back to the old one when the morph starts
  // (the "flash"). Only when settled AND the target matches do we recompute the
  // plain layout (so resize/hover reflect immediately).
  if (animatingRef.current) return frame
  if (signatureOf(targetFragments) !== signatureRef.current) return frame
  return idleFrame()
}
